import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Select,
  Input,
  VStack,
  HStack,
  Text,
  Spinner,
  Checkbox,
  Flex,
  IconButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Collapse,
  Textarea,
} from "@chakra-ui/react";
import { Header, FilterProto, ColumnInfo, ObservationColumnProto } from "build-ts/metadata";
import { BenchmarkType, FilterOperator, AggregationType } from "build-ts/enums";
import { BenchmarkEntityProto, BenchmarkEntityProto_DefinitionMapping } from "build-ts/knowledege_graph";
import { AddIcon, DeleteIcon, EditIcon } from "@chakra-ui/icons";
import * as MetricService from "../../services/MetricService";
import * as DataModelService from "../../services/DataModelService";
import showToast from "../../hooks/useCustomToast";
import { DateFilter, DatePeriodEnum } from "build-ts/relative_date";
import { DateFilterCategory } from "utils/enum";
import { ModelInfo } from "build-ts/metadata";
import { epochConverterSecond } from "../../utils/dateUtils";
import ObservationComponent from "../../containers/configuration/ObservationContainer";
import { useConfigurationContext } from "context/ConfigurationContext";
import { useEditorShellTokens } from "utils/editorShellStyles";

interface FilterWithValues {
  column: Header;
  values: string[];
  operator: FilterOperator;
}

interface MetricDefinitionMapping {
  mapping: BenchmarkEntityProto_DefinitionMapping;
  metricDetails?: Header;
  isLoading: boolean;
}

interface EditBenchmarkComponentProps {
  benchmark: BenchmarkEntityProto;
  onClose: () => void;
  onSave?: () => void;

  // For saving the Benchmark
  triggerSubmit?: boolean;
  setTriggerSubmit?: (value: boolean) => void;
  onReloadKnowledgeGraph?: () => void;
  compact?: boolean;
}

const EditBenchmarkComponent: React.FC<EditBenchmarkComponentProps> = ({
  benchmark,
  onClose,
  onSave,
  triggerSubmit,
  setTriggerSubmit,
  onReloadKnowledgeGraph,
  compact,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [benchmarkName, setBenchmarkName] = useState<string>("");
  const [benchmarkDescription, setBenchmarkDescription] = useState<string>("");
  const [benchmarkType, setBenchmarkType] = useState<BenchmarkType | undefined>(undefined);
  const [selectedGranularity, setSelectedGranularity] = useState<DatePeriodEnum | undefined>(undefined);

  // For filter selection UI
  const [selectedKGColumn, setSelectedKGColumn] = useState<Header | null>(null);
  const [columnValues, setColumnValues] = useState<string[]>([]);
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [isValuesLoading, setIsValuesLoading] = useState<boolean>(false);
  const [showFilterSelector, setShowFilterSelector] = useState<boolean>(false);

  // For storing multiple filters
  const [filters, setFilters] = useState<FilterWithValues[]>([]);

  // For definition mappings
  const [definitionMappings, setDefinitionMappings] = useState<MetricDefinitionMapping[]>([]);

  // New state for definition mapping
  const [showDefinitionModal, setShowDefinitionModal] = useState(false);
  const [availableMetrics, setAvailableMetrics] = useState<Header[]>([]);
  const [selectedMetricForDefinition, setSelectedMetricForDefinition] = useState<string>("");
  const [targetDataModelHeaders, setTargetDataModelHeaders] = useState<Header[]>([]);
  const [selectedTargetModelId, setSelectedTargetModelId] = useState<string>("");
  const [targetModelInfo, setTargetModelInfo] = useState<ModelInfo | null>(null);
  const [isLoadingTargetModel, setIsLoadingTargetModel] = useState<boolean>(false);

  // States for observation component
  const [selectedObservationColumn, setSelectedObservationColumn] = useState<Header | null>(null);
  const [selectedFilterColumn, setSelectedFilterColumn] = useState<ColumnInfo | null>(null);
  const [selectedAggregation, setSelectedAggregation] = useState<AggregationType | undefined>(AggregationType.SUM);
  const [selectedOperator, setSelectedOperator] = useState<FilterOperator | undefined>(FilterOperator.EQ);
  const [inputValue, setInputValue] = useState<string>("");
  const [columnNames, setColumnNames] = useState<string[]>([]);
  const [selectedColumnValues, setSelectedColumnValues] = useState<string[]>([]);
  const [selectedFilterColumns, setSelectedFilterColumns] = useState<FilterProto[]>([]);
  const [selectedObservationColumns, setSelectedObservationColumns] = useState<ObservationColumnProto[]>([]);
  const [dateFilter, setDateFilter] = useState<DateFilter | undefined>(undefined);
  const [dateFilterCategory, setDateFilterCategory] = useState<DateFilterCategory>(DateFilterCategory.FIXED);
  const [isSavingDefinition, setIsSavingDefinition] = useState<boolean>(false);
  const [editFilter, setEditFilter] = useState<boolean>(false);
  const [editingFilterIndex, setEditingFilterIndex] = useState<number>(-1);
  const [editingFilterValues, setEditingFilterValues] = useState<string[]>([]);

  const { fetchKgColumns, kgColumns } = useConfigurationContext();
  const t = useEditorShellTokens(compact ?? false);

  // Fetch KG columns on component mount
  useEffect(() => {
    setIsLoading(true);
    fetchKgColumns();
    setIsLoading(false);
  }, []);

  // Fetch metric details for each definition mapping
  useEffect(() => {
    if (benchmark?.benchmark_type === BenchmarkType.TARGET_BENCHMARK &&
      benchmark.definition_mapping.length > 0) {

      // Create initial state for definition mappings
      const initialMappings = benchmark.definition_mapping.map(mapping => ({
        mapping,
        isLoading: true
      }));

      setDefinitionMappings(initialMappings);

      // Fetch metric details for each mapping
      benchmark.definition_mapping.forEach(async (mapping, index) => {
        if (mapping.metric_id) {
          try {
            const metricDetails = await MetricService.getMetricById(mapping.metric_id, false);
            if (metricDetails) {
              setDefinitionMappings(prevMappings => {
                const newMappings = [...prevMappings];
                newMappings[index] = {
                  ...newMappings[index],
                  metricDetails: metricDetails.header,
                  isLoading: false
                };
                return newMappings;
              });
            }
          } catch (error) {
            console.error("Error fetching metric details:", error);
            // Mark as not loading even if there was an error
            setDefinitionMappings(prevMappings => {
              const newMappings = [...prevMappings];
              newMappings[index] = {
                ...newMappings[index],
                isLoading: false
              };
              return newMappings;
            });
          }
        }
      });
    } else {
      setDefinitionMappings([]);
    }
  }, [benchmark]);

  // Fetch KG column values when column is selected
  useEffect(() => {
    if (selectedKGColumn?.id) {
      fetchColumnValues();
    } else {
      setColumnValues([]);
      setSelectedValues([]);
    }
  }, [selectedKGColumn]);

  // Update form when benchmark is loaded
  useEffect(() => {
    if (benchmark) {
      setBenchmarkName(benchmark.header?.name || "");
      setBenchmarkDescription(benchmark.header?.description || "");
      setBenchmarkType(benchmark.benchmark_type);
      setSelectedGranularity(benchmark.granularity);

      // Extract all filters for VERSUS_BENCHMARK
      if (
        benchmark.benchmark_type === BenchmarkType.VERSUS_BENCHMARK &&
        benchmark.versus_filters.length > 0
      ) {
        const extractedFilters: FilterWithValues[] = benchmark.versus_filters
          .filter(filter => filter.kg_column_header)
          .map(filter => ({
            column: filter.kg_column_header!,
            values: filter.values,
            operator: filter.operator || FilterOperator.EQ
          }));

        setFilters(extractedFilters);
      } else {
        setFilters([]);
      }

      resetFilterSelector();
    }
  }, [benchmark]);

  // Fetch available metrics and target models for definition mapping
  useEffect(() => {
    const fetchAvailableMetrics = async () => {
      try {
        const metrics = await MetricService.listMetrics("");
        if (metrics) {
          setAvailableMetrics(metrics);
        }
      } catch (error) {
        showToast("Error fetching metrics", "", "error");
      }
    };

    const fetchTargetModels = async () => {
      try {
        const models = await DataModelService.getDataModelHeaders(false);
        if (models) {
          setTargetDataModelHeaders(models);
        }
      } catch (error) {
        showToast("Error fetching target models", "", "error");
      }
    };

    if (showDefinitionModal) {
      fetchAvailableMetrics();
      fetchTargetModels();
    }
  }, [showDefinitionModal]);

  // Reset definition form when modal is closed
  useEffect(() => {
    if (!showDefinitionModal) {
      resetDefinitionForm();
    }
  }, [showDefinitionModal]);

  // Handle target model selection
  const handleTargetModelSelect = async (id: string) => {
    setSelectedTargetModelId(id);

    if (id) {
      setIsLoadingTargetModel(true);
      try {
        const modelInfo = await DataModelService.getModelById(id);
        if (modelInfo) {
          setTargetModelInfo(modelInfo);
        }
      } catch (error) {
        showToast("Error fetching target model info", "", "error");
      } finally {
        setIsLoadingTargetModel(false);
      }
    } else {
      setTargetModelInfo(null);
    }
  };

  // Reset definition form
  const resetDefinitionForm = () => {
    setSelectedMetricForDefinition("");
    setSelectedTargetModelId("");
    setTargetModelInfo(null);
    setSelectedObservationColumn(null);
    setSelectedFilterColumn(null);
    setSelectedAggregation(AggregationType.SUM);
    setSelectedOperator(FilterOperator.EQ);
    setInputValue("");
    setColumnNames([]);
    setSelectedColumnValues([]);
    setSelectedFilterColumns([]);
    setSelectedObservationColumns([]);
    setDateFilter(undefined);
    setDateFilterCategory(DateFilterCategory.FIXED);
  };

  // Handle save definition mapping
  const handleSaveDefinitionMapping = async () => {
    if (!benchmark || !selectedMetricForDefinition || !selectedTargetModelId) {
      showToast("Please select a metric and target model", "", "error");
      return;
    }

    if (selectedObservationColumns.length === 0) {
      showToast("Please add at least one observation column", "", "error");
      return;
    }

    setIsSavingDefinition(true);
    try {
      // Create new definition mapping
      const newMapping = BenchmarkEntityProto_DefinitionMapping.create({
        metric_id: selectedMetricForDefinition,
        benchmark_definition: {
          content: {
            observation_columns: selectedObservationColumns,
            filters: selectedFilterColumns,
          }
        }
      });

      // Fetch the metric details to display in the UI
      const metricDetails = await MetricService.getMetricById(selectedMetricForDefinition, false);

      // Check if the metric already exists in the mappings
      const existingMappingIndex = definitionMappings.findIndex(
        mapping => mapping.mapping.metric_id === selectedMetricForDefinition
      );

      if (existingMappingIndex >= 0) {
        // Override existing mapping
        setDefinitionMappings(prevMappings => {
          const newMappings = [...prevMappings];
          newMappings[existingMappingIndex] = {
            mapping: newMapping,
            metricDetails: metricDetails?.header,
            isLoading: false
          };
          return newMappings;
        });
        showToast("Definition mapping updated", "", "success");
      } else {
        // Add new mapping
        setDefinitionMappings(prevMappings => [
          ...prevMappings,
          {
            mapping: newMapping,
            metricDetails: metricDetails?.header,
            isLoading: false
          }
        ]);
        showToast("Definition mapping added", "", "success");
      }

      setShowDefinitionModal(false);
    } catch (error) {
      showToast("Error adding definition mapping", "", "error");
    } finally {
      setIsSavingDefinition(false);
    }
  };

  const resetFilterSelector = () => {
    setSelectedKGColumn(null);
    setSelectedValues([]);
    setShowFilterSelector(false);
  };

  const fetchColumnValues = async () => {
    setIsValuesLoading(true);
    try {
      if (selectedKGColumn?.id) {
        const values = await DataModelService.getKGColumnValues(selectedKGColumn.id);
        if (values) {
          setColumnValues(values);
        } else {
          setColumnValues([]);
        }
      }
    } catch (error) {
      showToast("Error fetching column values", "", "error");
    } finally {
      setIsValuesLoading(false);
    }
  };

  const handleToggleValue = (value: string) => {
    if (selectedValues.includes(value)) {
      setSelectedValues(selectedValues.filter((v) => v !== value));
    } else {
      setSelectedValues([...selectedValues, value]);
    }
  };

  const handleAddFilter = () => {
    if (!selectedKGColumn || selectedValues.length === 0) {
      showToast("Please select a KG column and at least one value", "", "error");
      return;
    }
    // Add new filter
    setFilters([
      ...filters,
      {
        column: selectedKGColumn,
        values: selectedValues,
        operator: FilterOperator.EQ
      }
    ]);

    // Reset filter selector
    resetFilterSelector();
  };

  const handleRemoveFilter = (index: number) => {
    const newFilters = [...filters];
    newFilters.splice(index, 1);
    setFilters(newFilters);
  };

  // Handle edit filter
  const handleEditFilter = (filterIndex: number) => {
    const filter = filters[filterIndex];
    if (filter) {
      setEditingFilterIndex(filterIndex);
      setEditingFilterValues([...filter.values]);
      setEditFilter(true);

      // Fetch column values for the filter's column
      setSelectedKGColumn(filter.column);
    }
  };

  // Handle save edited filter
  const handleSaveEditedFilter = () => {
    if (editingFilterIndex >= 0 && editingFilterValues.length > 0) {
      const updatedFilters = [...filters];
      updatedFilters[editingFilterIndex] = {
        ...updatedFilters[editingFilterIndex],
        values: editingFilterValues
      };
      setFilters(updatedFilters);
      handleCancelEditFilter();
      showToast("Filter updated successfully", "", "success");
    }
  };

  // Handle cancel edit filter
  const handleCancelEditFilter = () => {
    setEditFilter(false);
    setEditingFilterIndex(-1);
    setEditingFilterValues([]);
    setSelectedKGColumn(null);
    setColumnValues([]);
    setSelectedValues([]);
  };

  // Handle toggle value for editing
  const handleToggleEditValue = (value: string) => {
    if (editingFilterValues.includes(value)) {
      setEditingFilterValues(editingFilterValues.filter((v) => v !== value));
    } else {
      setEditingFilterValues([...editingFilterValues, value]);
    }
  };

  const handleRemoveDefinitionMapping = async (index: number) => {
    if (!benchmark) return;

    setIsSaving(true);
    try {
      // Create a copy of the benchmark without the mapping to be removed
      const updatedBenchmark = { ...benchmark };
      const newDefinitionMappings = [...updatedBenchmark.definition_mapping];
      newDefinitionMappings.splice(index, 1);
      updatedBenchmark.definition_mapping = newDefinitionMappings;

      // Save the updated benchmark
      const savedBenchmark = await MetricService.saveBenchmark(updatedBenchmark);
      if (savedBenchmark) {
        showToast("Definition mapping removed successfully", "", "success");

        // Update the UI
        setDefinitionMappings(prevMappings => {
          const newMappings = [...prevMappings];
          newMappings.splice(index, 1);
          return newMappings;
        });
      }
    } catch (error) {
      showToast("Error removing definition mapping", "", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveBenchmark = async () => {
    if (!benchmark) return;

    if (!benchmarkName.trim()) {
      showToast("Benchmark name is required", "", "error");
      return;
    }

    if (
      benchmarkType === BenchmarkType.VERSUS_BENCHMARK &&
      filters.length === 0
    ) {
      showToast("Please add at least one filter", "", "error");
      return;
    }

    if (
      benchmarkType === BenchmarkType.TARGET_BENCHMARK &&
      definitionMappings.length === 0
    ) {
      showToast("Please add at least one definition for the target benchmark", "", "error");
      return;
    }

    setIsSaving(true);
    try {
      // Update benchmark properties
      const updatedBenchmark = { ...benchmark };

      if (updatedBenchmark.header) {
        updatedBenchmark.header.name = benchmarkName;
        updatedBenchmark.header.description = benchmarkDescription;
      }

      updatedBenchmark.benchmark_type = benchmarkType;

      // Set granularity for TARGET_BENCHMARK
      if (benchmarkType === BenchmarkType.TARGET_BENCHMARK) {
        updatedBenchmark.granularity = selectedGranularity;
      } else {
        updatedBenchmark.granularity = undefined;
      }

      // Update filters for VERSUS_BENCHMARK
      if (benchmarkType === BenchmarkType.VERSUS_BENCHMARK) {
        // Convert filters to FilterProto objects
        const versusFilters = filters.map(filter =>
          FilterProto.create({
            kg_column_header: filter.column,
            operator: filter.operator,
            values: filter.values,
            calculated_date_ranges: [],
          })
        );

        updatedBenchmark.versus_filters = versusFilters;
      } else {
        updatedBenchmark.versus_filters = [];
      }

      // Update definition mappings for TARGET_BENCHMARK
      if (benchmarkType === BenchmarkType.TARGET_BENCHMARK) {
        updatedBenchmark.definition_mapping = definitionMappings.map(item => item.mapping);
      } else {
        updatedBenchmark.definition_mapping = [];
      }

      const savedBenchmark = await MetricService.saveBenchmark(updatedBenchmark);
      if (savedBenchmark) {
        showToast("Benchmark updated successfully", "", "success");
        onSave?.();
        onClose();
        // Trigger reload of Knowledge Graph
        if (onReloadKnowledgeGraph) {
          onReloadKnowledgeGraph();
        }
      }
    } catch (error) {
      showToast("Error updating benchmark", "", "error");
    } finally {
      setIsSaving(false);
    }
  };

  // Helper function to format aggregation type for display
  const getAggregationName = (type?: AggregationType) => {
    switch (type) {
      case AggregationType.SUM:
        return "SUM";
      case AggregationType.AVERAGE:
        return "AVG";
      case AggregationType.MIN:
        return "MIN";
      case AggregationType.MAX:
        return "MAX";
      case AggregationType.COUNT:
        return "COUNT";
      default:
        return "SUM";
    }
  };

  // Helper function to render all filters with proper handling of values and date filters
  const renderAllFilters = (filters: FilterProto[]) => {
    if (!filters || filters.length === 0) return "";

    const filterTexts = filters.map(filter => {
      const columnName = filter.logical_column_header?.name || "Unknown Column";

      if (filter.values && filter.values.length > 0) {
        // Handle regular values
        return `${columnName} = ${filter.values.join(", ")}`;
      } else if (filter.date_filter && filter.date_filter.reference_date_epoc_sec) {
        // Handle date filter
        const dateStr = epochConverterSecond(filter.date_filter.reference_date_epoc_sec);
        return `${columnName} = ${dateStr}`;
      }
      return `${columnName} = (no value)`;
    });
    return ` where ${filterTexts.join(" AND ")}`;
  };


  // Watch for triggerSubmit and call submit when triggered
  useEffect(() => {
    if (triggerSubmit) {
      handleSaveBenchmark();
      // Reset the trigger after submitting
      if (setTriggerSubmit) {
        setTriggerSubmit(false);
      }
    }
  }, [triggerSubmit]);

  const SectionBar = ({ label }: { label: string }) => (
    <Box
      bg="gray.50"
      px={t.sectionPx}
      py={t.sectionPy}
      borderTopWidth="1px"
      borderBottomWidth="1px"
      borderColor="gray.100"
    >
      <Text
        fontWeight="600"
        fontSize={t.sectionTitleSize}
        color="gray.600"
        textAlign="left"
        letterSpacing="normal"
        mb={0}
      >
        {label}
      </Text>
    </Box>
  );

  return (
    <Box position="relative">
      {isSaving && (
        <Box
          position="absolute"
          top={0} left={0} right={0} bottom={0}
          bg="rgba(255,255,255,0.75)"
          zIndex={10}
          display="flex"
          alignItems="center"
          justifyContent="center"
          borderRadius="md"
        >
          <VStack spacing={2}>
            <Spinner size="md" color="purple.500" thickness="3px" />
            <Text fontSize={t.bodyText} fontWeight="600" color="purple.600" mb={0}>Saving…</Text>
          </VStack>
        </Box>
      )}

      <VStack spacing={0} align="stretch" opacity={isSaving ? 0.6 : 1}>
        {isLoading ? (
          <Box textAlign="center" py={10}>
            <Spinner size="md" color="purple.500" />
            <Text mt={2} color="gray.500" fontSize={t.bodyText}>Loading benchmark…</Text>
          </Box>
        ) : benchmark ? (
          <Box>
            {/* Core properties */}
            <SectionBar label="Semantics" />
            <VStack
              spacing={t.vStackSpacing}
              align="stretch"
              px={t.contentPx}
              pt={t.contentPt}
              pb={t.contentPb}
              bg="white"
            >
              <HStack spacing={t.vStackSpacing}>
                <FormControl flex="1">
                  <FormLabel {...t.label}>Name</FormLabel>
                  <Input
                    {...t.field}
                    size={t.inputSize}
                    value={benchmarkName}
                    onChange={(e) => setBenchmarkName(e.target.value)}
                    placeholder="Benchmark name"
                    isDisabled={isSaving}
                  />
                </FormControl>
                <FormControl flex="1">
                  <FormLabel {...t.label}>Type</FormLabel>
                  <Select
                    {...t.field}
                    size={t.inputSize}
                    value={benchmarkType}
                    onChange={(e) => setBenchmarkType(Number(e.target.value) as BenchmarkType)}
                    isDisabled
                  >
                    <option value={BenchmarkType.VERSUS_BENCHMARK}>Versus Benchmark</option>
                    <option value={BenchmarkType.TARGET_BENCHMARK}>Target Benchmark</option>
                    <option value={BenchmarkType.NO_BENCHMARK}>No Benchmark</option>
                  </Select>
                </FormControl>
              </HStack>

              <FormControl>
                <FormLabel {...t.label}>Context</FormLabel>
                <Textarea
                  {...t.field}
                  size={t.inputSize}
                  rows={t.textareaRows}
                  value={benchmarkDescription}
                  onChange={(e) => setBenchmarkDescription(e.target.value)}
                  placeholder="Describe what this benchmark represents and how it should be used…"
                  resize="vertical"
                />
              </FormControl>

              {benchmarkType === BenchmarkType.TARGET_BENCHMARK && (
                <FormControl maxW="240px">
                  <FormLabel {...t.label}>Granularity</FormLabel>
                  <Select
                    {...t.field}
                    size={t.inputSize}
                    value={selectedGranularity}
                    onChange={(e) => setSelectedGranularity(Number(e.target.value) as DatePeriodEnum)}
                    placeholder="Select granularity"
                    isDisabled={isSaving}
                  >
                    <option value={DatePeriodEnum.DAY}>Day</option>
                    <option value={DatePeriodEnum.WEEK}>Week</option>
                    <option value={DatePeriodEnum.MONTH}>Month</option>
                    <option value={DatePeriodEnum.QUARTER}>Quarter</option>
                    <option value={DatePeriodEnum.YEAR}>Year</option>
                  </Select>
                </FormControl>
              )}
            </VStack>

            {/* Filters / Definitions Section */}
            <SectionBar label={benchmarkType === BenchmarkType.TARGET_BENCHMARK ? "Definition Mappings" : "Filters"} />
            <Box>
              <Box px={t.contentPx} pt={t.contentPt} pb={t.contentPb} bg="white">
                {/* Definition Mappings for TARGET_BENCHMARK */}
                {benchmarkType === BenchmarkType.TARGET_BENCHMARK && (
                  <Box>
                    <Flex justifyContent="space-between" alignItems="center" mb={t.compact ? 2 : 3}>
                      <Text fontWeight="medium" fontSize={t.bodyText}>Definition Mappings</Text>
                      <HStack>
                        <Text fontSize={t.bodyText} color="gray.600">
                          {definitionMappings.length} mapping{definitionMappings.length !== 1 ? "s" : ""}
                        </Text>
                        <Button
                          size="sm"
                          colorScheme="blue"
                          leftIcon={<AddIcon />}
                          onClick={() => setShowDefinitionModal(true)}
                          isDisabled={isSaving}
                        >
                          Add Definition
                        </Button>
                      </HStack>
                    </Flex>

                    {definitionMappings.length > 0 ? (
                      <TableContainer>
                        <Table variant="simple" size="sm">
                          <Thead bg="gray.50">
                            <Tr>
                              <Th>Metric</Th>
                              <Th>Definition</Th>
                              <Th width="50px"></Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {definitionMappings.map((mappingItem, index) => (
                              <Tr key={index}>
                                <Td>
                                  {mappingItem?.isLoading ? (
                                    <Spinner size="xs" mr={2} />
                                  ) : (
                                    mappingItem?.metricDetails?.name || mappingItem?.mapping?.metric_id || "Unknown Metric"
                                  )}
                                </Td>
                                <Td>
                                  {mappingItem?.mapping?.benchmark_definition?.content ? (
                                    <Text fontSize={t.bodyText}>
                                      {mappingItem.mapping.benchmark_definition.content.observation_columns?.[0]?.logical_column_header?.name
                                        ? `${mappingItem.mapping.benchmark_definition.content.observation_columns[0].logical_column_header.name} 
                                         (${getAggregationName(mappingItem.mapping.benchmark_definition.content.observation_columns[0].aggregation)})`
                                        : "No definition"}
                                      {renderAllFilters(mappingItem.mapping.benchmark_definition.content.filters)}
                                    </Text>
                                  ) : (
                                    <Text fontSize={t.bodyText} color="gray.500">No definition</Text>
                                  )}
                                </Td>
                                <Td>
                                  <IconButton
                                    aria-label="Remove mapping"
                                    icon={<DeleteIcon />}
                                    size="sm"
                                    variant="ghost"
                                    colorScheme="red"
                                    onClick={() => handleRemoveDefinitionMapping(index)}
                                    isDisabled={isSaving}
                                  />
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Box p={3} borderWidth="1px" borderRadius="md" bg="gray.50">
                        <Text color="gray.500" fontSize={t.bodyText}>
                          No definition mappings found. This benchmark is not connected to any metrics.
                        </Text>
                      </Box>
                    )}

                    {/* Add Definition Form - Inline collapsible section */}
                    <Collapse in={showDefinitionModal} animateOpacity>
                      <Box
                        borderWidth="1px"
                        borderRadius="md"
                        p={4}
                        mt={4}
                        bg="blue.50"
                        borderColor="blue.200"
                      >
                        <VStack spacing={4} align="stretch">
                          <Flex justify="space-between" align="center">
                            <Text fontWeight="bold" fontSize={t.panelHeading} color="blue.800">
                              Add Definition Mapping
                            </Text>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setShowDefinitionModal(false)}
                            >
                              Cancel
                            </Button>
                          </Flex>

                          {/* Select Metric */}
                          <FormControl isRequired>
                            <FormLabel>Select Metric</FormLabel>
                            <Select
                              placeholder="Select a metric"
                              value={selectedMetricForDefinition}
                              onChange={(e) => setSelectedMetricForDefinition(e.target.value)}
                              bg="white"
                            >
                              {availableMetrics.map((metric) => (
                                <option key={metric.id} value={metric.id}>
                                  {metric.name}
                                </option>
                              ))}
                            </Select>
                            {/* Warning message for duplicate metric */}
                            {selectedMetricForDefinition &&
                              definitionMappings.some(mapping => mapping.mapping.metric_id === selectedMetricForDefinition) && (
                                <Text color="red.500" fontSize={t.bodyText} mt={2}>
                                  ⚠️ Warning: This metric already has a definition mapping. Proceeding will override the existing mapping.
                                </Text>
                              )}
                          </FormControl>

                          {/* Select Target Model */}
                          <FormControl isRequired>
                            <FormLabel>Select Target Model</FormLabel>
                            <Select
                              placeholder="Select a target model"
                              value={selectedTargetModelId}
                              onChange={(e) => handleTargetModelSelect(e.target.value)}
                              isDisabled={isLoadingTargetModel}
                              bg="white"
                            >
                              {targetDataModelHeaders.map((model) => (
                                <option key={model.id} value={model.id}>
                                  {model.name}
                                </option>
                              ))}
                            </Select>
                          </FormControl>

                          {/* Show loading state while fetching target model */}
                          {isLoadingTargetModel && (
                            <Box textAlign="center" py={4}>
                              <Spinner size="sm" />
                              <Text mt={2} fontSize={t.bodyText}>Loading target model...</Text>
                            </Box>
                          )}

                          {/* Observation Component once target model is selected */}
                          {targetModelInfo && (
                            <Box>
                              <Flex direction="row" gap={4}>
                                {/* Left side - Observations */}
                                <Box flex="1" bg="white" p={3} borderRadius="md" borderWidth="1px">
                                  <Text fontWeight="medium" mb={2} fontSize={t.bodyText}>Define Observations</Text>
                                  <ObservationComponent
                                    type="observation"
                                    columns={targetModelInfo.columns!}
                                    selectedObservationColumn={selectedObservationColumn}
                                    selectedFilterColumn={selectedFilterColumn}
                                    selectedAggregation={selectedAggregation}
                                    selectedOperator={selectedOperator}
                                    inputValue={inputValue}
                                    selectedColumnValues={selectedColumnValues}
                                    selectedFilterColumns={selectedFilterColumns}
                                    selectedObservationColumns={selectedObservationColumns}
                                    dateFilter={dateFilter}
                                    dateFilterCategory={dateFilterCategory}
                                    isLoading={isLoading}
                                    setSelectedObservationColumn={setSelectedObservationColumn}
                                    setColumnNames={setColumnNames}
                                    setSelectedFilterColumn={setSelectedFilterColumn}
                                    setSelectedColumnValues={setSelectedColumnValues}
                                    setSelectedAggregation={setSelectedAggregation}
                                    setSelectedOperator={setSelectedOperator}
                                    setInputValue={setInputValue}
                                    setSelectedFilterColumns={setSelectedFilterColumns}
                                    setSelectedObservationColumns={setSelectedObservationColumns}
                                    setDateFilter={setDateFilter}
                                    setDateFilterCategory={setDateFilterCategory}
                                    setIsLoading={setIsLoading}
                                  />
                                </Box>

                                {/* Right side - Filters */}
                                <Box flex="1" bg="white" p={3} borderRadius="md" borderWidth="1px">
                                  <Text fontWeight="medium" mb={2} fontSize={t.bodyText}>Define Filters</Text>
                                  <ObservationComponent
                                    type="filter"
                                    columns={targetModelInfo.columns!}
                                    columnNames={columnNames}
                                    selectedObservationColumn={selectedObservationColumn}
                                    selectedFilterColumn={selectedFilterColumn}
                                    selectedAggregation={selectedAggregation}
                                    selectedOperator={selectedOperator}
                                    inputValue={inputValue}
                                    selectedColumnValues={selectedColumnValues}
                                    selectedFilterColumns={selectedFilterColumns}
                                    selectedObservationColumns={selectedObservationColumns}
                                    dateFilter={dateFilter}
                                    dateFilterCategory={dateFilterCategory}
                                    isLoading={isLoading}
                                    setSelectedObservationColumn={setSelectedObservationColumn}
                                    setColumnNames={setColumnNames}
                                    setSelectedFilterColumn={setSelectedFilterColumn}
                                    setSelectedColumnValues={setSelectedColumnValues}
                                    setSelectedAggregation={setSelectedAggregation}
                                    setSelectedOperator={setSelectedOperator}
                                    setInputValue={setInputValue}
                                    setSelectedFilterColumns={setSelectedFilterColumns}
                                    setSelectedObservationColumns={setSelectedObservationColumns}
                                    setDateFilter={setDateFilter}
                                    setDateFilterCategory={setDateFilterCategory}
                                    setIsLoading={setIsLoading}
                                  />
                                </Box>
                              </Flex>
                            </Box>
                          )}

                          {/* Footer buttons for definition form */}
                          <Flex justify="flex-end" gap={3} pt={4} borderTop="1px solid" borderColor="blue.200">
                            <Button
                              variant="outline"
                              onClick={() => setShowDefinitionModal(false)}
                            >
                              Cancel
                            </Button>
                            <Button
                              colorScheme="blue"
                              onClick={handleSaveDefinitionMapping}
                              isLoading={isSavingDefinition}
                              isDisabled={
                                !selectedMetricForDefinition ||
                                !selectedTargetModelId ||
                                selectedObservationColumns.length === 0
                              }
                            >
                              Add Definition
                            </Button>
                          </Flex>
                        </VStack>
                      </Box>
                    </Collapse>
                  </Box>
                )}

                {/* Filters for VERSUS_BENCHMARK */}
                {benchmarkType === BenchmarkType.VERSUS_BENCHMARK && (
                  <Box>
                    <Flex justify="space-between" align="center" mb={t.compact ? 2 : 3}>
                      <Text fontWeight="medium" fontSize={t.bodyText}>Filters</Text>
                      <Button
                        size="sm"
                        colorScheme="blue"
                        leftIcon={<AddIcon />}
                        onClick={() => setShowFilterSelector(true)}
                        isDisabled={isSaving}
                      >
                        Add Filter
                      </Button>
                    </Flex>

                    {/* Display existing filters */}
                    {filters.length > 0 ? (
                      <TableContainer mb={4}>
                        <Table variant="simple" size="sm">
                          <Thead bg="gray.50">
                            <Tr>
                              <Th>Column</Th>
                              <Th>Values</Th>
                              <Th width="50px"></Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {filters.map((filter, index) => (
                              <Tr key={index}>
                                <Td>{filter.column.name}</Td>
                                <Td>
                                  <Text fontSize={t.bodyText} noOfLines={2} m={0}>
                                    {filter.values.join(", ")}
                                  </Text>
                                </Td>
                                <Td>
                                  <HStack>
                                    <IconButton
                                      aria-label="Edit filter"
                                      icon={<EditIcon />}
                                      size="sm"
                                      variant="ghost"
                                      colorScheme="green"
                                      onClick={() => handleEditFilter(index)}
                                      isDisabled={isSaving}
                                    />
                                    <IconButton
                                      aria-label="Remove filter"
                                      icon={<DeleteIcon />}
                                      size="sm"
                                      variant="ghost"
                                      colorScheme="red"
                                      onClick={() => handleRemoveFilter(index)}
                                      isDisabled={isSaving}
                                    />
                                  </HStack>
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Box p={3} borderWidth="1px" borderRadius="md" bg="gray.50" mb={4}>
                        <Text color="gray.500" fontSize={t.bodyText}>
                          No filters added. Add a filter to define your benchmark.
                        </Text>
                      </Box>
                    )}

                    {/* Filter selector */}
                    {showFilterSelector && (
                      <Box
                        borderWidth="1px"
                        borderRadius="md"
                        p={4}
                        mb={4}
                        bg="gray.50"
                      >
                        <VStack spacing={4} align="stretch">
                          <FormControl>
                            <FormLabel>KG Column</FormLabel>
                            <Select
                              value={selectedKGColumn?.id || ""}
                              onChange={(e) => {
                                const selectedId = e.target.value;
                                const selected = kgColumns?.find((col) => col.id === selectedId) || null;
                                setSelectedKGColumn(selected);
                                setSelectedValues([]);
                              }}
                              placeholder="Select KG column"
                              isDisabled={isSaving}
                            >
                              {kgColumns?.map((column) => {
                                const isAlreadyUsed = filters.some(filter => filter.column.id === column.id);
                                const shouldDisable = filters.length > 0 && !isAlreadyUsed;
                                return (
                                  <option key={column.id} value={column.id} disabled={shouldDisable}>
                                    {column.name}
                                  </option>
                                );
                              })}
                            </Select>
                          </FormControl>

                          {selectedKGColumn && (
                            <FormControl>
                              <FormLabel>Select Values</FormLabel>
                              {isValuesLoading ? (
                                <Box textAlign="center" py={4}>
                                  <Spinner size="sm" color="blue.500" />
                                  <Text mt={2} fontSize={t.bodyText}>Loading values...</Text>
                                </Box>
                              ) : columnValues.length > 0 ? (
                                <Box
                                  maxH="200px"
                                  overflowY="auto"
                                  border="1px"
                                  borderColor="gray.200"
                                  borderRadius="md"
                                  p={2}
                                  bg="white"
                                >
                                  {columnValues.map((value, index) => (
                                    <Box key={index} py={1}>
                                      <Checkbox
                                        isChecked={selectedValues.includes(value)}
                                        onChange={() => handleToggleValue(value)}
                                        colorScheme="blue"
                                        isDisabled={isSaving}
                                      >
                                        {value}
                                      </Checkbox>
                                    </Box>
                                  ))}
                                </Box>
                              ) : (
                                <Text color="gray.500">No values available for this column</Text>
                              )}
                            </FormControl>
                          )}

                          <HStack spacing={3} justify="flex-end">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => resetFilterSelector()}
                              isDisabled={isSaving}
                            >
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              colorScheme="blue"
                              onClick={handleAddFilter}
                              isDisabled={!selectedKGColumn || selectedValues.length === 0 || isSaving}
                            >
                              Add
                            </Button>
                          </HStack>
                        </VStack>
                      </Box>
                    )}

                    {/* Edit filter */}
                    {editFilter && editingFilterIndex >= 0 && (
                      <Box
                        borderWidth="1px"
                        borderRadius="md"
                        p={4}
                        mb={4}
                        bg="yellow.50"
                        borderColor="yellow.200"
                      >
                        <VStack spacing={4} align="stretch">
                          <Flex justify="space-between" align="center">
                            <Text fontWeight="bold" fontSize={t.panelHeading} color="yellow.800">
                              Edit Filter: {filters[editingFilterIndex]?.column?.name}
                            </Text>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={handleCancelEditFilter}
                            >
                              Cancel
                            </Button>
                          </Flex>

                          <FormControl>
                            <FormLabel>Select Values</FormLabel>
                            {isValuesLoading ? (
                              <Box textAlign="center" py={4}>
                                <Spinner size="sm" color="blue.500" />
                                <Text mt={2} fontSize={t.bodyText}>Loading values...</Text>
                              </Box>
                            ) : columnValues.length > 0 ? (
                              <Box
                                maxH="200px"
                                overflowY="auto"
                                border="1px"
                                borderColor="gray.200"
                                borderRadius="md"
                                p={2}
                                bg="white"
                              >
                                {columnValues.map((value, index) => (
                                  <Box key={index} py={1}>
                                    <Checkbox
                                      isChecked={editingFilterValues.includes(value)}
                                      onChange={() => handleToggleEditValue(value)}
                                      colorScheme="blue"
                                      isDisabled={isSaving}
                                    >
                                      {value}
                                    </Checkbox>
                                  </Box>
                                ))}
                              </Box>
                            ) : (
                              <Text color="gray.500">No values available for this column</Text>
                            )}
                          </FormControl>

                          <HStack spacing={3} justify="flex-end">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={handleCancelEditFilter}
                              isDisabled={isSaving}
                            >
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              colorScheme="blue"
                              onClick={handleSaveEditedFilter}
                              isDisabled={editingFilterValues.length === 0 || isSaving}
                            >
                              Save Changes
                            </Button>
                          </HStack>
                        </VStack>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        ) : (
          <Box textAlign="center" py={10}>
            <Text fontSize={t.bodyText} color="gray.400">No benchmark data available.</Text>
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default EditBenchmarkComponent;